import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    Typography,
    Breadcrumbs,
    Link as MuiLink,
    Tabs,
    Tab,
    Box,
    IconButton,
    Tooltip,
    useTheme,
    Snackbar,
    Alert as MuiAlert // Renamed Alert
} from '@mui/material';
import {
    CalculateOutlined as CalculateOutlinedIcon,
    HomeOutlined as HomeOutlinedIcon,
    NavigateNext as NavigateNextIcon,
    InfoOutlined as InfoOutlinedIcon // Needed for tooltip example on switch if used
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import DOMPurify from 'dompurify';

// Your other component/action imports
import MultipleChoiceQuestion from './MultipleChoice'; // Adjust path as needed
import { fetchExamForPreview } from '../../actions/auth'; // Adjust path as needed
import Footer from './Footer'; // Adjust path as needed
import CalculatorComponent from '../../components/CalculatorComponent'; // Adjust path as needed
import LoadingOverlay from '../../components/LoadingOverlay'; // Adjust path as needed
import { tokens } from "../../theme"; // Adjust path as needed
import './testpages.css'; // Adjust path as needed

// --- Main Component ---
const Testpagepreview = () => {
    const { examId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // --- State hooks ---
    const [currentExam, setCurrentExam] = useState({ name: '', modules: [] });
    const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedAnswers, setSelectedAnswers] = useState({}); // Stores correct answers for preview
    const [calculatorVisible, setCalculatorVisible] = useState(false);
    const [referenceVisible, setReferenceVisible] = useState(false);
    const [isMinimapVisible, setIsMinimapVisible] = useState(false);
    const [markedForReview, setMarkedForReview] = useState({});
    const [crossedOut, setCrossedOut] = useState({});
    const [crossedIcon, setCrossedIcon] = useState(false);
    // Note: userInput and inputError might not be needed for preview
    // const [userInput, setUserInput] = useState('');
    // const [inputError, setInputError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' }); // Keep snackbar state if used for errors
    const [calculatorPosition, setCalculatorPosition] = useState({ x: 0, y: 0 });
    const [referencePosition, setReferencePosition] = useState({ x: 0, y: 0 });
    const [isCalculatorExpanded, setIsCalculatorExpanded] = useState(false);

    // --- Refs ---
    const myCalculatorRef = useRef(null);
    const minimapRef = useRef(null);

    // --- Selectors ---
    const user = useSelector(state => state.auth.user); // Keep user if needed by Footer or other parts

    // --- Derived State ---
    // Use useMemo for potentially expensive calculations or derivations
    const currentModule = useMemo(() => currentExam?.modules?.[currentModuleIndex], [currentExam, currentModuleIndex]);
    const currentQuestion = useMemo(() => currentModule?.questions?.[currentQuestionIndex], [currentModule, currentQuestionIndex]);
    const isMCQuestion = useMemo(() => currentQuestion?.question_type === 'MC', [currentQuestion]);
    // Selected value in preview mode should reflect the stored correct answer
    const selectedValue = useMemo(() => String(selectedAnswers[currentQuestion?.id] ?? ''), [selectedAnswers, currentQuestion]);

    // --- Callbacks ---

    // Sets the 'selectedAnswers' state with the correct answers from the fetched exam
    const setCorrectAnswers = useCallback((exam) => {
        if (!exam || !exam.modules) return;
        const correctAnswers = {};
        exam.modules.forEach(module => {
            module.questions?.forEach(question => {
                // Assuming model provides correct answer fields like this
                correctAnswers[question.id] = question.question_type === 'NI'
                    ? question.correct_numerical_answer // Adjust field name if different
                    : question.correct_choice;          // Adjust field name if different
            });
        });
        setSelectedAnswers(correctAnswers);
    }, []); // No dependencies needed if it only processes the passed 'exam'

    const fetchExamStatus = useCallback(async () => {
        setLoading(true);
        setError(''); // Clear previous errors
        try {
            const exam = await fetchExamForPreview(examId); // Removed navigate dependency unless fetch action requires it
            if (exam && exam.modules) {
                setCurrentExam(exam);
                setCorrectAnswers(exam);
                // Reset indexes on successful load
                setCurrentModuleIndex(0);
                setCurrentQuestionIndex(0);
            } else {
                setError('Exam data not found or is incomplete.');
                setCurrentExam({ name: 'Error', modules: [] });
            }
        } catch (err) {
            console.error('Error fetching exam preview:', err);
            setError(`Failed to load exam data: ${err.message || 'Unknown error'}`);
            setCurrentExam({ name: 'Error', modules: [] });
        } finally {
            setLoading(false);
        }
    }, [examId, setCorrectAnswers]); // Add navigate back if fetchExamForPreview uses it

    // Fetch data on mount
    useEffect(() => {
        fetchExamStatus();
    }, [fetchExamStatus]);

    // Handlers for UI interactions
    const handleModuleChange = useCallback((event, newValue) => {
        if (newValue !== currentModuleIndex) {
            setCurrentModuleIndex(newValue);
            setCurrentQuestionIndex(0); // Reset question index when module changes
            // Clear interaction states for the new module? (optional)
            // setMarkedForReview({});
            // setCrossedOut({});
        }
    }, [currentModuleIndex]);

    const handleNextQuestion = useCallback(() => {
        const questionCount = currentModule?.questions?.length ?? 0;
        if (currentQuestionIndex < questionCount - 1) {
            setCurrentQuestionIndex(prevIndex => prevIndex + 1);
        }
        // Maybe navigate to next module preview automatically? Or just stop.
    }, [currentQuestionIndex, currentModule]);

    const handlePreviousQuestion = useCallback(() => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prevIndex => prevIndex - 1);
        }
    }, [currentQuestionIndex]);

    const handleSelectQuestion = useCallback((index) => {
        setCurrentQuestionIndex(index);
    }, []);

    // Preview doesn't involve saving answers, but might involve marking/crossing out
    const isQuestionAnswered = useCallback((questionId) => {
        // In preview, the correct answer is always 'selected' / known
        return selectedAnswers[questionId] !== undefined;
    }, [selectedAnswers]);

    const toggleMarkForReview = useCallback((questionId) => {
        setMarkedForReview(prev => ({ ...prev, [questionId]: !prev[questionId] }));
    }, []);

    const isMarkedReview = useCallback((questionId) => {
        return markedForReview[questionId] === true;
    }, [markedForReview]);

    const toggleCrossedOut = useCallback((questionId, choiceIndex) => {
       const numberOfChoices = Object.keys(currentQuestion?.choices || {}).length || 4; // Default to 4 if choices aren't loaded
       setCrossedOut(prev => {
            const currentChoices = prev[questionId] || Array(numberOfChoices).fill(false);
            const newChoices = [...currentChoices];
            if(choiceIndex >= 0 && choiceIndex < newChoices.length){
                 newChoices[choiceIndex] = !newChoices[choiceIndex];
            }
            return { ...prev, [questionId]: newChoices };
        });
    }, [currentQuestion?.choices]);

    const toggleTrueCrossedOut = useCallback((questionId, choiceIndex) => {
        const numberOfChoices = Object.keys(currentQuestion?.choices || {}).length || 4;
        setCrossedOut(prev => {
            const currentChoices = prev[questionId] || Array(numberOfChoices).fill(false);
            const newChoices = [...currentChoices];
             if(choiceIndex >= 0 && choiceIndex < newChoices.length){
                 newChoices[choiceIndex] = false; // Specifically uncross
            }
            return { ...prev, [questionId]: newChoices };
        });
    }, [currentQuestion?.choices]);

    const toggleCrossIcon = useCallback(() => setCrossedIcon(prev => !prev), []);
    const toggleMinimap = useCallback(() => setIsMinimapVisible(prev => !prev), []);
    const setMiniMapFalse = useCallback(() => setIsMinimapVisible(false), []);
    const toggleCalculator = useCallback(() => setCalculatorVisible(prev => !prev), []);
    const toggleReference = useCallback(() => setReferenceVisible(prev => !prev), []);
    const handleCloseSnackbar = useCallback((_event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar(prev => ({ ...prev, open: false }));
    }, []);


    // --- Sanitization ---
    // Memoize sanitizer function if needed, though DOMPurify is generally fast
    const sanitizeHtml = useCallback((html) => {
        // Basic processing, ensure DOMPurify handles the heavy lifting
        const processed = html?.replace(/<p>/gi, '<span>').replace(/<\/p>/gi, '</span>') || '';
        return DOMPurify.sanitize(processed);
    }, []);


    // --- Render Logic ---
    if (loading) return <LoadingOverlay text="Loading Exam Preview..." />;
    // Display error prominently if loading failed
    if (error) return <Typography color="error" sx={{ p: 3, textAlign: 'center' }}>{error}</Typography>;
    // Handle case where exam loaded but modules/questions might still be missing
    if (!currentExam || !currentModule || !currentQuestion) {
        return <Typography sx={{ p: 3, textAlign: 'center' }}>Exam data is unavailable or incomplete for preview.</Typography>;
    }

    // --- FINAL JSX ---
    return (
        <>
            {/* --- Improved Top Navigation --- */}
            <AppBar position="sticky" elevation={1} sx={{ backgroundColor: theme.palette.background.paper }}>
                <Toolbar variant="dense"> {/* Use dense variant for a shorter toolbar */}
                    {/* Breadcrumbs on the Left */}
                    <Breadcrumbs
                        aria-label="breadcrumb"
                        separator={<NavigateNextIcon fontSize="small" />}
                        sx={{ flexGrow: { xs: 1, sm: 'unset' } }} // Allow breadcrumbs to take space on small screens
                    >
                        <MuiLink
                            component="button" // Use button for onClick action
                            underline="hover"
                            color="inherit"
                            onClick={() => navigate('/manage-exams')}
                            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: theme.palette.text.secondary }}
                        >
                            <HomeOutlinedIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                            Manage Exams
                        </MuiLink>
                        {/* Exam Name (Not a link in preview context usually) */}
                        <Typography color={theme.palette.text.secondary} noWrap>
                           Preview: {currentExam.name || 'Exam'}
                        </Typography>
                        {/* Current Module Name */}
                        <Typography color={theme.palette.text.primary} sx={{ fontWeight: 'medium' }} noWrap>
                            {currentModule.name || 'Module'}
                        </Typography>
                    </Breadcrumbs>

                    {/* Spacer for larger screens */}
                    <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' } }} />

                    {/* Actions on the Right */}
                    <Box>
                        <Tooltip title="Toggle Calculator">
                            <IconButton onClick={toggleCalculator} color="primary" aria-label="toggle calculator">
                                <CalculateOutlinedIcon />
                            </IconButton>
                        </Tooltip>
                         {/* Example: Reference Toggle - Add icon import if needed */}
                         {/*
                         <Tooltip title="Toggle Reference Sheet">
                            <IconButton onClick={toggleReference} color="primary" aria-label="toggle reference sheet">
                                <ArticleOutlinedIcon />
                            </IconButton>
                        </Tooltip>
                        */}
                    </Box>
                </Toolbar>

                {/* Tabs Below Toolbar */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: theme.palette.background.default }}>
                    <Tabs
                        value={currentModuleIndex}
                        onChange={handleModuleChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        aria-label="Exam Modules"
                        sx={{
                             '& .MuiTabs-indicator': {
                                backgroundColor: theme.palette.primary.main,
                            }
                        }}
                    >
                        {currentExam.modules.map((module, index) => (
                            <Tab
                                key={module.id || index}
                                label={module.name || `Module ${index + 1}`}
                                id={`module-tab-${index}`}
                                aria-controls={`module-tabpanel-${index}`}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: theme.typography.fontWeightRegular,
                                    '&.Mui-selected': {
                                        fontWeight: theme.typography.fontWeightMedium,
                                         color: theme.palette.primary.main,
                                    },
                                    '&:focus-visible': { // More specific focus state for accessibility
                                       outline: `2px solid ${theme.palette.primary.main}`,
                                       outlineOffset: '1px',
                                       backgroundColor: theme.palette.action.hover,
                                    },
                                }}
                            />
                        ))}
                    </Tabs>
                </Box>
            </AppBar>
            {/* --- End Improved Top Navigation --- */}


            {/* --- Main Content Area --- */}
            {/* Use Box for main container with flex properties */}
            <Box className="flex-container" sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 112px)' }}> {/* Adjust height based on AppBar+Tabs height */}

                <Box className="page-wrapper" id="sandbox" sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden' }}> {/* Make wrapper flex and grow */}
                    {/* Left Panel (Question Text) */}
                    {/* Ensure these inner boxes handle overflow correctly */}
                    <Box className="sidebar-wrapper middle" data-simplebar sx={{ overflowY: 'auto', flexBasis: '50%', flexShrink: 0, position: 'relative',height: '100%' }}>
                        <Box className="sidebar-content" sx={{ p: 2 }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(currentQuestion.text) }} />
                         {/* Add Resizer element if needed */}
                    </Box>

                    {/* Right Panel (Question Description/Input) */}
                    <Box className="page-content-wrapper middle" data-simplebar sx={{ overflowY: 'auto', flexGrow: 1, p: 2, position: 'relative',height: '100%'}}>
                         {/* Add Resizer element if needed */}
                        <MultipleChoiceQuestion
                            question={sanitizeHtml(currentQuestion.description)}
                            options={Object.values(currentQuestion.choices || {})}
                            selectedValue={selectedValue} // Show the correct answer
                            // No onValueChange for preview
                            questionNumber={currentQuestionIndex + 1}
                            isMCQuestion={isMCQuestion}
                            // No onBlur needed
                            markedForReview={markedForReview[currentQuestion.id]}
                            onMarkForReview={() => toggleMarkForReview(currentQuestion.id)}
                            crossedOut={crossedOut[currentQuestion.id]}
                            toggleCrossedOut={(index) => toggleCrossedOut(currentQuestion.id, index)}
                            toggleTrueCrossedOut={(index) => toggleTrueCrossedOut(currentQuestion.id, index)}
                            crossIcon={crossedIcon}
                            toggleCrossIcon={toggleCrossIcon}
                            readOnly={true} // Indicate component should be in read-only state
                            statistics={currentQuestion.statistics} // Pass stats if needed
                        />
                    </Box>
                </Box>


                {/* Calculator and Reference Components - Render outside the main flex scroll area */}
                <CalculatorComponent
                    isVisible={calculatorVisible}
                    toggleVisibility={toggleCalculator}
                    position={calculatorPosition}
                    onPositionChange={setCalculatorPosition}
                    isExpanded={isCalculatorExpanded}
                    setIsExpanded={setIsCalculatorExpanded}
                    myCalculatorRef={myCalculatorRef}
                    isCalculator={true}
                />
                <CalculatorComponent
                    isVisible={referenceVisible}
                    toggleVisibility={toggleReference}
                    position={referencePosition}
                    onPositionChange={setReferencePosition}
                    myCalculatorRef={myCalculatorRef} // Might not need ref here
                    isCalculator={false} // Indicates reference sheet mode
                />

                {/* Footer */}
                {/* Ensure Footer doesn't overlap content; place it after the main content Box */}
                <Footer
                    user={user} // Pass user if needed for display
                    isMinimapVisible={isMinimapVisible}
                    minimapRef={minimapRef}
                    currentExam={currentModule} // Pass current module
                    currentQuestionIndex={currentQuestionIndex}
                    isQuestionAnswered={isQuestionAnswered}
                    toggleMinimap={toggleMinimap}
                    handleSelectQuestion={handleSelectQuestion}
                    // openReviewPage={openReviewPage} // Not needed for preview
                    handlePreviousQuestion={handlePreviousQuestion}
                    handleNextQuestion={handleNextQuestion}
                    // showReviewPage={showReviewPage} // Not needed
                    setMiniMapFalse={setMiniMapFalse}
                    isMarkedReview={isMarkedReview}
                    // Add any other props Footer needs
                />
            </Box> {/* End flex-container Box */}

            {/* Snackbar for potential error feedback */}
            <Snackbar
                 open={snackbar.open}
                 autoHideDuration={6000}
                 onClose={handleCloseSnackbar}
                 anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <MuiAlert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled" elevation={6}>
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>
        </>
    );
};

export default Testpagepreview;